// src/ui/CookieBanner.jsx — the cookie question, asked once.
//
// 🟥 IT IS NOT A "WE USE COOKIES, OK" BAR. Nothing has been set when this
//    appears — the analytics script has not loaded and Google has not been
//    contacted — so Decline is a real choice with a real effect, and the wording
//    says so rather than implying the decision has already been made.
//
// 🟥 AND DECLINE IS NOT HIDDEN. Both buttons are the same size, in the same
//    place, one visually secondary because one of them has to be. A banner whose
//    "no" is a grey link in a corner is a dark pattern, and it is also the thing
//    regulators point at first.
//
// 🟢 NEVER ON A GUEST PAGE. A guest is a stranger who followed a link to read
//    one conversation; measuring them is not on the table (see analytics.js for
//    why the token makes that unsafe as well as impolite), so there is nothing
//    to ask them about and no banner to put in their way.

import React, { useEffect, useState } from 'react';
import { Lock, X } from './icons';
import {
  consentState, grant, deny, analyticsAvailable, isMeasurablePath, onConsentChange,
} from '../services/analytics';
import './cookies.css';

export default function CookieBanner() {
  const [choice, setChoice] = useState(() => consentState());
  const [more, setMore] = useState(false);

  useEffect(() => onConsentChange(setChoice), []);

  /* 🟢 the banner animates in a beat after the page settles, so it does not
     compete with the first paint of whatever the person came for */
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), 600);
    return () => clearTimeout(t);
  }, []);

  if (!analyticsAvailable()) return null;              // nothing to consent to
  if (!isMeasurablePath(window.location.pathname)) return null;
  if (choice) return null;                             // already answered

  return (
    <div className={`ck ${shown ? 'in' : ''}`} role="dialog" aria-live="polite"
      aria-label="Cookies on this site">
      <div className="ck-card">
        <span className="ck-ico"><Lock width={16} height={16} /></span>

        <div className="ck-body">
          <b>Cookies for visitor statistics</b>
          <p>
            We would like to count visits with Google Analytics, which sets a cookie
            in your browser. It tells us how many people opened the site and roughly
            which country they were in — never who you are, and never anything from
            your conversations.
          </p>

          {more && (
            <ul className="ck-more">
              <li>Nothing has been set yet — declining means Google is never contacted.</li>
              <li>Your messages, contacts and account are never sent to Google.</li>
              <li>Temporary chat links are excluded entirely.</li>
              <li>You can change your mind at any time from the bottom of this screen.</li>
              <li>Details: <a href="/cookies" target="_blank" rel="noopener">Cookie Policy</a> · <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a></li>
            </ul>
          )}

          <button type="button" className="ck-link" onClick={() => setMore((v) => !v)}>
            {more ? 'Show less' : 'What exactly is collected?'}
          </button>
        </div>

        <div className="ck-acts">
          <button type="button" className="ck-btn ghost" onClick={deny}>Decline</button>
          <button type="button" className="ck-btn" onClick={grant}>Accept</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The way back to the decision.
 *
 * 🟥 A CONSENT YOU CANNOT WITHDRAW IS NOT A CONSENT. Once the banner is answered
 *    it never returns, so without this the choice would be permanent — and
 *    "you can change your mind" in the banner text would be a lie. Small, out of
 *    the way, and always reachable.
 */
export function CookieSettings() {
  const [choice, setChoice] = useState(() => consentState());
  useEffect(() => onConsentChange(setChoice), []);

  if (!analyticsAvailable()) return null;
  if (!isMeasurablePath(window.location.pathname)) return null;
  if (!choice) return null;                            // the banner is still up

  return (
    <button
      type="button"
      className="ck-settings"
      onClick={() => (choice === 'granted' ? deny() : grant())}
      title={choice === 'granted'
        ? 'Stop counting this visit and delete the cookie'
        : 'Allow visitor statistics'}
    >
      {choice === 'granted' ? <X width={12} height={12} /> : <Lock width={12} height={12} />}
      {choice === 'granted' ? 'Turn off visitor statistics' : 'Allow visitor statistics'}
    </button>
  );
}
